/**
 * GetApi - Generic GET request wrapper for Lenders App
 * Includes VS-Client-Id header and credentials for cookie-based auth
 *
 * PRIV-20.3: Added optional purpose parameter for explicit audit trail
 */

import { apiResponse } from "./Types";

/**
 * PRIV-20.3: Purpose types for lender actions
 * - lender_review: Default browsing, no specific intent
 * - underwriting: Actively evaluating a loan application
 * - credit_check: Accessing credit report or credit-related data
 * - fraud_prevention: Investigating suspicious activity
 */
export type RequestPurpose = 'lender_review' | 'underwriting' | 'credit_check' | 'fraud_prevention';

function GetApi<returntype>(
  endpoint: string,
  onSuccess: (data: returntype) => void,
  onFail: (data: object) => void,
  onReject: (response: string) => void,
  purpose?: RequestPurpose // PRIV-20.3: Optional explicit purpose
) {
  // Add debugging to track API calls
  const apiPath = endpoint.replace(import.meta.env.VITE_API_URL || "", "");
  console.log(`🌐 GET API Call: ${apiPath}`, {
    timestamp: new Date().toISOString(),
    endpoint: apiPath,
    fullUrl: endpoint,
    purpose: purpose || 'auto-inferred', // PRIV-20.3: Log purpose for debugging
  });

  // PRIV-20.3: Build headers with optional purpose
  const headers: Record<string, string> = {
    "VS-Client-Id": import.meta.env.VITE_VS_CLIENT_ID,
  };
  if (purpose) {
    headers["x-request-purpose"] = purpose;
  }

  const promise = fetch(endpoint, {
    method: "GET",
    headers,
    credentials: "include",
  });

  return promise
    .then(async (putResp) => {
      // Handle auth errors - redirect to logout
      if (putResp.status === 401 || putResp.status === 403) {
        console.warn(`⚠️ [GetApi] Session expired (${putResp.status}), redirecting to logout...`);
        // Redirect to logout page
        window.history.replaceState({}, '', '?view=logout&reason=timeout');
        window.dispatchEvent(new PopStateEvent('popstate'));
        throw `Session expired (${putResp.status})`;
      }

      if (!putResp.ok) {
        // Try to parse error response body for user-friendly messages
        try {
          const errorData = await putResp.json();
          // If backend returns structured error with message, use it
          if (errorData && errorData.message) {
            throw errorData; // Throw the whole error object
          }
        } catch (parseError) {
          // If JSON parsing fails, fall back to status text
        }
        throw `HTTP ${putResp.status}: ${putResp.statusText}`;
      }
      const jsonData: any = await putResp.json();
      if (
        typeof jsonData.status !== "string" ||
        typeof jsonData.data !== "object"
      ) {
        throw await putResp.text();
      }
      return jsonData as apiResponse;
    })
    .then((resp) => {
      const status = resp.status;
      if (status === "success") {
        onSuccess(resp.data as returntype);
      } else if (status === "fail") {
        onFail(resp.data as object);
      } else {
        throw "unexpected response status";
      }
    })
    .catch((error) => {
      // If error is an object with a message property (from backend), use it
      if (error && typeof error === "object" && error.message) {
        onReject(error.message);
      } else if (typeof error === "string") {
        onReject(error);
      } else {
        onReject("unhandled error");
      }
    });
}

export default GetApi;

